import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from "./context/AuthContext";
import { CategoryProvider } from "./context/CategoryContext";
import Header from "./Component/Header";
import Footer from "./Component/Footer";
import AdminRoute from "./Component/AdminRoutes";
import CustomerRoute from "./Component/CustomerRoute";
import LoadingSpinner from "./Component/LoadingSpinner";
import NotFound from "./Pages/NotFound";

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner size="lg" variant="blue" />
  </div>
);

// Lazy load routes for code splitting
// Public routes
const Home = lazy(() => import("./Pages/Home/Home"));
const Category = lazy(() => import("./Pages/Category/Category"));
const About = lazy(() => import("./Pages/About/About"));
const ProductCatalog = lazy(() => import("./Pages/Product/product"));
const ProductDetail = lazy(() => import("./Pages/Product/ProductDetail"));
const Login = lazy(() => import("./Pages/Auth/Login"));
const Signup = lazy(() => import("./Pages/Auth/Signup"));
const AdminLogin = lazy(() => import("./Pages/Admin/Adminlogin"));
const PrivacyPolicy = lazy(() => import("./Pages/Legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./Pages/Legal/TermsOfService"));

// Customer routes
const Profile = lazy(() => import("./Pages/Profile/profile"));
const Cart = lazy(() => import("./Pages/Cart/Cart"));

// Admin routes (heavier bundle)
const Admin = lazy(() => import("./Pages/Admin/admin"));
const AddProduct = lazy(() => import("./Pages/CRUD/AddProduct"));
const AddCategory = lazy(() => import("./Pages/CRUD/AddCategory"));
const UpdateProduct = lazy(() => import("./Pages/CRUD/UpdateProduct"));
const UpdateCategory = lazy(() => import("./Pages/CRUD/UpdateCategory"));

// Component to conditionally render Footer
function AppContent() {
  const location = useLocation();
  
  // Hide footer on admin pages (except admin login page)
  const isAdminPage = location.pathname.startsWith('/admin') && location.pathname !== '/admin/login';
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/category" element={<Category />} />
          <Route path="/about" element={<About />} />
          <Route path="/product" element={<ProductCatalog />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          
          {/* Protected Customer Routes */}
          <Route path="/profile" element={
            <CustomerRoute>
              <Profile />
            </CustomerRoute>
          } />
          
          <Route path="/cart" element={
            <CustomerRoute>
              <Cart />
            </CustomerRoute>
          } />
          
          
          {/* Protected Admin Routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          } />
          
          {/* Protected CRUD Routes */}
          <Route path="/admin/add-product" element={
            <AdminRoute>
              <AddProduct />
            </AdminRoute>
          } />
          <Route path="/admin/add-category" element={
            <AdminRoute>
              <AddCategory />
            </AdminRoute>
          } />
          <Route path="/admin/update-product/:productId" element={
            <AdminRoute>
              <UpdateProduct />
            </AdminRoute>
          } />
          <Route path="/admin/update-category/:categoryId" element={
            <AdminRoute>
              <UpdateCategory />
            </AdminRoute>
          } />

          {/* Catch-all: unmatched routes get 404 page with noindex (SEO) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!isAdminPage && <Footer />}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        enableMultiContainer={false}
        limit={5}
      />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CategoryProvider>
          <Router>
            <AppContent />
          </Router>
        </CategoryProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
